import React, { ReactElement, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchHouseAdCampaign } from "../actions/sale";
import Banner from "../containers/helpers/genericBanner";
import Countdown from "../containers/helpers/Countdown";
import { getCurrentHouseAdCampaignSelector } from "../selectors/currentSaleData";

export type Props = {
  router: any,
  placement: keyof bb.state.ISale["houseAdCampaign"],
  parallax?: boolean
}

export enum BannerAssetKeys {
  banner="banner",
  mobile_banner="mobile_banner",
  url="url",
  mobile_url="mobile_url",
  showSaleCountdown="showSaleCountdown",
  saleCountdownPrompt="saleCountdownPrompt",
  saleCountdownEndDates="saleCountdownEndDates",
  saleCountdown24HrMax="saleCountdown24HrMax",
}

export function getAsString(val:string|undefined|null, defaultVal?:string):string|undefined {
  if (typeof val === "string") {
    return val
  }
  else {
    return defaultVal
  }
}

export function mapAssets(assets:bb.model.sale.IHouseAdAsset[]):Record<BannerAssetKeys, string|undefined|null> {
  const map = {} as Record<BannerAssetKeys, string|undefined|null>

  for (let asset of assets) {
    if (asset.assetKey in BannerAssetKeys) {
      map[asset.assetKey as BannerAssetKeys] = asset.content
    }
  }

  return map
}


export function getAsBool(val:string|undefined|null, defaultVal:boolean=false):boolean {
  if (typeof val === "string") {
    const isTrue = val.toLowerCase() === "true"
    const isFalse = val.toLowerCase() === "false"

    if (isTrue) {
      return true
    }
    else if (isFalse) {
      return false
    }
    else {
      throw new Error("Unexpected boolean string value: "+val)
    }
  }
  else {
    return defaultVal
  }
}

function findUpcomingEndDateInCSV(csv:string):Date|null {
  const now = new Date()
  const rows = csv.split(",")
  const upcomingDates = rows.filter(row => {
    const date = new Date(row)
    return date.getTime() > now.getTime()
  })
  if (upcomingDates.length === 0) {
    console.log("No upcoming dates found in CSV: "+csv)
    return null
  }
  return new Date(upcomingDates[0])
}

export default function PromotionalBanner ({router, placement, parallax}:Props):ReactElement|null {
  const dispatch = useDispatch();
  const campaign = useSelector(getCurrentHouseAdCampaignSelector(placement));

  useEffect(() => {
    if (!campaign) {
      dispatch(fetchHouseAdCampaign(placement));
    }
  }, [campaign, dispatch, placement]);

  if (campaign === null) {
    return null;
  }

  const assetMap = mapAssets(campaign.assets)

  const desktopImageUrl = getAsString(assetMap.banner)
  const mobileImageUrl = getAsString(assetMap.mobile_banner)
  const clickUrl = getAsString(assetMap.url)
  const mobileClickUrl = getAsString(assetMap.mobile_url, clickUrl)
  const showCountdown = getAsBool(assetMap.showSaleCountdown)
  const prompt = getAsString(assetMap.saleCountdownPrompt)
  const timerBreakpoints = getAsString(assetMap.saleCountdownEndDates)
  const timer24HrMax = getAsBool(assetMap.saleCountdown24HrMax)

  let endDate = campaign.endDate
  if(timerBreakpoints) {
    const upcomingEndDate = findUpcomingEndDateInCSV(timerBreakpoints)
    if (upcomingEndDate) {
      endDate = upcomingEndDate
    }
  }
  let timeRemaining = endDate.getTime() - (new Date()).getTime()

  // if timeRemaining is more than 24 hours and timer24HrMax is true, set the days remaining to 0, 
  // while keeping the hours/minutes/seconds so it looks like the sale ends that day
  if(timeRemaining > 24*60*60*1000 && timer24HrMax) {
    while(timeRemaining > 24*60*60*1000) {
      timeRemaining -= 24*60*60*1000
    }
  }

  const external = clickUrl?.startsWith("http")

  return (
    <Banner
      router={router}
      desktopImage={desktopImageUrl}
      mobileImage={mobileImageUrl}
      external={external}
      redirectURL={clickUrl}
      mobileRedirectURL={mobileClickUrl}
      parallax={parallax}
      mobileBreakpoint={620}
      bannerStyle={{
        width: "100%",
        position: "relative"
      }}
    >
      {showCountdown && (
        <Countdown
          prompt={prompt}
          saleTimeRemaining={timeRemaining}
        />
      )}
    </Banner>
  )
}
